import context from "react-bootstrap/esm/AccordionContext";
import { deviceKeyTypes, deviceTemplates } from "../../devices/deviceTemplates";
import { flatDeviceTemplate, keyTypeOptions } from "views/pages/hrms/devices/deviceService";

const groupedDeviceTemplate = Object.values(
  flatDeviceTemplate.reduce((acc, entry) => {
    const [vendorRaw] = entry.split("/");
    const vendor = vendorRaw.charAt(0).toUpperCase() + vendorRaw.slice(1);

    if (!acc[vendor]) acc[vendor] = { label: vendor, options: [] };

    acc[vendor].options.push({
      label: entry,     // or entry.split("/")[1] if you want model-only label
      value: entry
    });

    return acc;
  }, {})
);

export const deviceForm = {
  table: "v_devices",
  required: ["device_address"],

  labels: {
    // Basic Info
    device_address: "Address",
    device_label: "Label",
    device_vendor: "Vendor",
    device_template: "Template",
    device_profile_uuid: "Profile",
    device_location: "Location",
    device_enabled: "Enabled",
    device_description: "Description",

    // Lines
    line_number: "Line Number",
    server_address: "Server Address",
    label: "Line Label",
    display_name: "Display Name",
    user_id: "User ID",
    auth_id: "Auth ID",
    password: "Password",
    sip_port: "SIP Port",
    sip_transport: "Transport",
    register_expires: "Register Expires",
    shared_line: "Shared Line",
    line_enabled: "Line Enabled",

    // Keys
    device_key_category: "Category",
    device_key_id: "Key",
    device_key_type: "Type",
    device_key_line: "Line",
    device_key_value: "Value",
    device_key_label: "Label",

    // Settings
    device_setting_name: "Setting Name",
    device_setting_value: "Value",
    device_setting_enabled: "Enabled",
    device_setting_description: "Description",
  },

  sections: {
    "Basic Info": [
      "device_address",
      "device_label",
      "device_template",
      "device_profile_uuid",
      "device_vendor",
      "device_location",
      "device_enabled",
      "device_description",
    ],

    "Lines": [
      "line_number",
      "server_address",
      "label",
      "display_name",
      "user_id",
      "auth_id",
      "password",
      "sip_port",
      "sip_transport",
      "register_expires",
      "shared_line",
      "line_enabled",
    ],

    "Keys": [
      "device_key_category",
      "device_key_id",
      "device_key_type",
      "device_key_line",
      "device_key_value",
      "device_key_label",
    ],

    "Settings": [
      "device_setting_name",
      "device_setting_value",
      "device_setting_enabled",
      "device_setting_description",
    ],
  },

  types: {
    // Basic Info
    device_enabled: "boolean",
    device_vendor: "select",
    device_template: "select",
    device_profile_uuid: "select",

    // Lines
    line_number: "select",
    sip_transport: "select",
    line_enabled: "boolean",

    // Keys
    device_key_category: "select",
    device_key_id: "select",
    device_key_type: "select",
    device_key_line: "select",

    // Settings
    device_setting_enabled: "boolean",
  },

  options: {
    // Runtime-filled selects
    device_vendor: [],   // vendors
    device_template: groupedDeviceTemplate,
    device_profile_uuid: [],

    line_number: Array.from({ length: 99 }, (_, i) => String(i + 1)),
    sip_transport: [
      { label: "TCP", value: "tcp" },
      { label: "UDP", value: "udp" },
      { label: "TLS", value: "tls" },
      { label: "DNS SRV", value: "dns srv" },
    ],

    device_key_category: [
      "Line",
      "Memory",
      "Programmable",
      "Expansion 1",
      "Expansion 2",
      "Expansion 3",
      "Expansion 4",
      "Expansion 5",
      "Expansion 6",
    ],

    device_key_id: Array.from({ length: 255 }, (_, i) => String(i + 1)),
    device_key_line: Array.from({ length: 99 }, (_, i) => String(i + 1)),

    device_key_type: keyTypeOptions,
  },

  defaults: {
    device_enabled: "true",
    sip_transport: "TCP",
    sip_port: "5060",
    register_expires: "120",
    line_enabled: "true",
  },

  requiredSections: {
    Lines: ["line_number", "server_address"],
    Keys: ["device_key_type", "device_key_line"],
    Settings: ["device_setting_name", "device_setting_value"],
  },
};


export const deviceProfileForm = {
  table: "v_device_profiles",
  required: ["device_profile_name"],

  labels: {
    // Basic Info
    device_profile_name: "Name",
    device_profile_enabled: "Enabled",
    device_profile_description: "Description",

    // Keys
    profile_key_category: "Category",
    profile_key_id: "Key",
    profile_key_vendor: "Vendor",
    profile_key_type: "Type",
    profile_key_line: "Line",
    profile_key_value: "Value",
    profile_key_protected: "Protected",
    profile_key_label: "Label",

    // Settings
    profile_setting_name: "Name",
    profile_setting_value: "Value",
    profile_setting_enabled: "Enabled",
    profile_setting_description: "Description",
  },

  sections: {
    "Basic Info": ["device_profile_name", "device_profile_enabled", "device_profile_description"],
    "Keys": ["profile_key_category", "profile_key_id", "profile_key_vendor", "profile_key_type", "profile_key_line", "profile_key_value", "profile_key_protected", "profile_key_label"],
    "Settings": ["profile_setting_name", "profile_setting_value", "profile_setting_enabled", "profile_setting_description"],
  },

  types: {
    device_profile_enabled: "boolean",
    profile_key_category: "select",
    profile_key_type: "select",
    profile_key_line: "select",
    profile_key_id: "select",
    profile_key_vendor: "select",
    profile_setting_enabled: "boolean",
    profile_key_protected: "boolean",
  },

  options: {
    profile_key_type: ["Line", "Memory", "Programmable", "Expansion 1", "Expansion 2", "Expansion 3", "Expansion 4", "Expansion 5", "Expansion 6"],
    profile_key_line: Array.from({ length: 99 }, (_, i) => String(i + 1)),
    profile_key_category: ["Line", "Memory", "Programmable", "Expansion", "Expansion 1", "Expansion 2", "Expansion 3", "Expansion 4", "Expansion 5", "Expansion 6"],
    profile_key_id: Array.from({ length: 255 }, (_, i) => String(i + 1)),
  },

  defaults: {
    device_profile_enabled: "true",
    profile_setting_enabled: "true",
  },

  requiredSections: {
    Keys: ["profile_key_category", "profile_key_id", "profile_key_vendor", "profile_key_type", "profile_key_line"],
    Settings: ["profile_setting_name"],
  },
};

export const deviceVendorForm = {
  table: "v_device_vendors",
  required: ["name"],
  labels: {
    name: "Vendor Name",
    enabled: "Enabled",
    description: "Description",
  },
  types: {
    name: "text",
    enabled: "boolean",
    description: "text",
  },
  defaults: {
    enabled: "true",
    description: "",
  },
  sections: {
    "Basic Info": ["name", "enabled", "description"],
  },
};

export const extensionForm = {
  required: ["extension"],

  sections: {
    "Basic Info": [
      "extension",
      "password",
      "effective_caller_id_name",
      "effective_caller_id_number",
      "outbound_caller_id_name",
      "outbound_caller_id_number",
      "missed_call_app",
      "missed_call_email",
      "dialplan_permissions",
      "enabled",
      "user_context",
      "description",
      "extension_type",
    ],
    
    "Voicemail & Account": [
      "voicemail_password",
      "voicemail_enabled",
      "voicemail_mail_to",
      "voicemail_transcription_enabled",
      "voicemail_file",
      "voicemail_local_after_email"
    ],

    "Call Forward": {
      fields: [
        "forward_all_enabled",
        "forward_all_destination",
        "forward_busy_enabled",
        "forward_busy_destination",
        "forward_no_answer_enabled",
        "forward_no_answer_destination",
        "forward_user_not_registered_enabled",
        "forward_user_not_registered_destination"
      ], colSize: 3
    },

    "Device Provisioning": {
      advanced: true,
      fields: [
        "provision_device_uuid",
        "provision_line",
        "provision_template"
      ]
    },

    "Emergency": {
      advanced: true,
      fields: [
        "emergency_caller_id_name",
        "emergency_caller_id_number"
      ]
    },

    "Directory Settings": {
      advanced: true,
      fields: [
        "directory_first_name",
        "directory_last_name",
        "directory_visible",
        "directory_exten_visible"
      ]
    },

    "Limits": {
      advanced: true,
      fields: [
        "max_registrations",
        "limit_max",
        "limit_destination"
      ]
    },

    "Call / System Settings": {
      advanced: true,
      fields: [
        "call_timeout",
        "call_group",
        "call_screen_enabled",
        "user_record",
        "hold_music"
      ]
    }
  },

  defaults: {
    directory_visible: "true",
    directory_exten_visible: "true",
    voicemail_enabled: "true",
    voicemail_local_after_email: "true",
    call_screen_enabled: "false",
    call_timeout: "30",
    user_record: "Disabled",
    transcription_enabled: "false",
    enabled: true,
    voicemail_file: "attatch",
    limit_destination: "!USER_BUSY"
  },

  labels: {
    extension_type: "Type",
    missed_call_app: "Missed Call",
    missed_call_email: "Email Address",
    enabled: "Extension Enabled",
    provision_device_uuid: "Device Address",
    provision_line: "Line",
    provision_template: "Template"
  },

  types: {
    extension_type: "select",
    dialplan_permissions: "select",
    missed_call_app: "select",
    hold_music: "select",
    user_record: "select",

    transcription_enabled: "boolean",
    enabled: "boolean",
    voicemail_transcription_enabled: "boolean",
    voicemail_local_after_email: "boolean",
    call_screen_enabled: "boolean",

    directory_visible: "boolean",
    directory_exten_visible: "boolean",

    forward_all_enabled: "boolean",
    forward_busy_enabled: "boolean",
    forward_no_answer_enabled: "boolean",
    forward_user_not_registered_enabled: "boolean",

    voicemail_file: "select",

    provision_device_uuid: "select",
    provision_line: "select",
    provision_template: "select"
  },

  options: {
    user_record: [
      "Disabled",
      "All",
      "Local",
      "Inbound",
      "Outbound"
    ],

    missed_call_app: [
      "Email"
    ],

    toll_allow: [
      "Domestic",
      "International",
      "Local"
    ],

    extension_type: [
      "Default",
      "Virtual"
    ],

    hold_music: [
      { label: "Default", value: "local_stream://default" }
    ],

    voicemail_file: [
      { label: "Listen Link (Login Required)", value: "" },
      { label: "Download Link (No Login Required)", value: "link" },
      { label: "Audio File Attachment", value: "attatch" }
    ],

    provision_line: Array.from({ length: 99 }, (_, i) => String(i + 1)),

    provision_template: groupedDeviceTemplate
  }
};

export const extensionSettingsForm = {
  table: "v_extension_settings",
  required: ["extension", "extension_setting_type"],

  sections: {
    "Basic Info": [
      "extension",
      "extension_setting_type",
      "extension_setting_name",
      "extension_setting_value",
      "extension_setting_enabled",
      "extension_setting_description",
    ],
  },

  types: {
    extension_setting_enabled: "boolean",
    extension: "readonly",
  },

  labels: {
    extension_setting_category: "Type",
    extension_setting_subcategory: "Name",
    extension_setting_value: "Value",
    extension_setting_enabled: "Enabled",
    extension_setting_description: "Description",
  },
};

export const gatewayForm = {
  table: "v_gateways",
  required: ["gateway", "username", "password"],
  sections: {
    "Basic Info": ["gateway", "username", "password"],
    "SIP Settings": [
      "from_user",
      "from_domain",
      "proxy",
      "realm",
      "expire_seconds",
      "register",
      "retry_seconds",
      "context",
      "profile",
    ],
    "Status": ["enabled", "description"],
    "Registration Settings": {
      advanced: true,
      fields: [
        "auth_username",
        "extension",
        "register_transport",
        "register_proxy",
        "outbound_proxy"
      ]
    },

    "Contact Routing": {
      advanced: true,
      fields: [
        "distinct_to",
        "contact_params",
        "extension_in_contact",
        "caller_id_in_from"
      ]
    },

    "Caller ID Settings": {
      advanced: true,
      fields: [
        "sip_cid_type",
        "supress_cng"
      ]
    },

    "Codec": {
      advanced: true,
      fields: [
        "codec_prefs"
      ]
    },

    "Ping Monitoring": {
      advanced: true,
      fields: [
        "ping",
        "ping_min",
        "ping_max",
        "contact_in_ping"
      ]
    },

    "Channel Limit": {
      advanced: true,
      fields: [
        "channels"
      ]
    },

    "Network Hosting": {
      advanced: true,
      fields: [
        "hostname",
        //"domain"
      ]
    },

  },
  types: {
    register: "boolean",
    enabled: "boolean",
    profile: "select",
    distinct_to: "boolean",
    register_transport: "select",
    caller_id_in_from: "boolean",
    supress_cng: "boolean",
    extension_in_contact: "boolean",
    contact_in_ping: "boolean",
  },

  options: {
    profile: [
      "external",
      "external-ipv6",
      "internal",
      "internal-ipv6",
    ],
    register_transport: [
      "UDP",
      "TCP",
      "TLS",
    ],
  },
  defaults: {
    register: "true",
    enabled: "true",
    profile: "external",
    retry_seconds: "30",
    expire_seconds: "800",
    context: "public",
  },
};

export const userForm = {
  table: "v_users",
  required: ["username", "password", "confirm_password"],
  sections: {
    "Account Info": [
      "username",
      "password",
      "confirm_password",
      "email",
      "language",
      "time_zone",
      "status",
    ],
    "Personal Info": ["first_name", "last_name", "organization", "groups", "note"],
    "Login Settings": ["type", "default", "domain", "api_key", "enabled"],
  },
  types: {
    enabled: "boolean",
  },
};
